import { Component, inject, ViewEncapsulation } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '@envs/environment';
import { UserIconComponent } from 'app/shared/controls/user-icon/user-icon.component';
import { FeatureFlagType } from 'app/shared/models/config/feature-flag.type';
import { CognitoService } from 'app/shared/services/cognito.service';
import { ReleaseNotesDialogService } from 'app/shared/services/release-notes-dialog.service';
import { GlobalStore } from 'app/shared/store/global.store';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Ripple } from 'primeng/ripple';
import { Tooltip } from 'primeng/tooltip';
import { IfFeatureDirective } from '../../shared/directives/if-feature.directive';
import { IfHasAccessDirective } from '../../shared/directives/if-has-access.directive';
import { withoutTransition } from '../../shared/utils/change-theme.util';

@Component({
  selector: 'inpla-navbar',
  imports: [RouterLinkActive, RouterLink, Menu, UserIconComponent, Ripple, Tooltip, IfFeatureDirective, IfHasAccessDirective],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent {
  private readonly cognitoService: CognitoService = inject(CognitoService);
  private readonly releaseNotesDialogService: ReleaseNotesDialogService = inject(ReleaseNotesDialogService);
  private readonly router: Router = inject(Router);

  public readonly globalStore = inject(GlobalStore);
  public readonly FeatureFlagType = FeatureFlagType;

  public readonly menuItems: MenuItem[] = [
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      command: () => this.router.navigate(['/settings']),
      visible: this.globalStore.config()[FeatureFlagType.PROFILE_SETTINGS_ENABLED],
    },
    {
      label: 'Administration Panel',
      icon: 'pi pi-sliders-h',
      command: () => this.router.navigate(['/administration-panel']),
      visible: this.globalStore.config()[FeatureFlagType.PLATFORM_USAGE_PAGE_ENABLED] || this.globalStore.isIndagoAiTenant(),
    },
    {
      label: 'Billing',
      icon: 'pi pi-dollar',
      command: () => this.router.navigate(['/billing']),
      visible: this.globalStore.config()[FeatureFlagType.BILLINGS_PAGE_ENABLED],
    },
    {
      label: "What's new",
      icon: 'pi pi-info-circle',
      command: () => this.openRelaseNotesDialog(),
      visible: !this.globalStore.config()[FeatureFlagType.PROFILE_SETTINGS_ENABLED],
    },
    {
      label: 'Logout',
      icon: 'pi pi-power-off',
      command: () => this.cognitoService.signOut(),
    },
  ];

  public setDarkTheme(): void {
    withoutTransition(() => {
      this.changeTheme('dark-theme');
      this.globalStore.setTheme('dark-theme');
    });
  }

  public setLightTheme(): void {
    withoutTransition(() => {
      this.changeTheme('light-theme');
      this.globalStore.setTheme('light-theme');
    });
  }

  private openRelaseNotesDialog(): void {
    this.globalStore.getReleaseNote();
    this.releaseNotesDialogService.openRelaseNotesDialog(true, false);
  }

  private changeTheme(theme: string): void {
    const element = document.querySelector('html');
    localStorage.setItem('indago-theme', theme);
    document.body.classList.remove('dark-theme', 'light-theme');
    document.body.classList.add(theme);
    element?.classList.toggle(environment.theme.value);
  }
}
